import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getAdminPropertyStatusCounts } from '../lib/api';
import { useAuth } from './AuthContext';

const AdminCountsContext = createContext(null);

export function AdminCountsProvider({ children }) {
  const { isAdmin, user } = useAuth();
  const [counts, setCounts] = useState({
    pending_review: 0,
    approved: 0,
    rejected: 0,
    draft: 0,
    paused: 0,
    suspended: 0,
    published: 0
  });
  const [loading, setLoading] = useState(false);

  const fetchCounts = useCallback(async () => {
    if (!isAdmin) return;
    
    setLoading(true);
    try {
      const response = await getAdminPropertyStatusCounts();
      setCounts(response.data);
    } catch (error) {
      console.error('Failed to fetch admin property counts:', error);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  // Fetch on mount and when admin status changes
  useEffect(() => {
    if (isAdmin && user) {
      fetchCounts();
    }
  }, [isAdmin, user, fetchCounts]);

  // Function to refresh counts (called after approve/reject actions)
  const refreshCounts = useCallback(() => {
    fetchCounts();
  }, [fetchCounts]);

  const value = {
    counts,
    loading,
    refreshCounts,
    pendingReviewCount: counts.pending_review
  };

  return (
    <AdminCountsContext.Provider value={value}>
      {children}
    </AdminCountsContext.Provider>
  );
}

export function useAdminCounts() {
  const context = useContext(AdminCountsContext);
  if (!context) {
    // Return default values if not within provider (non-admin users)
    return {
      counts: {},
      loading: false,
      refreshCounts: () => {},
      pendingReviewCount: 0
    };
  }
  return context;
}
